/** The carve-out design stages. The stage lens refines the carve-out perspective only. */
export const STAGES = ['perimeter', 'day1', 'day2'];
export const PERSPECTIVE_BUCKET = {
    'as-is': null,
    remain: 'Remain',
    'carve-out': 'Carve-out',
};
export function isBuildUp(p) {
    return p.isBuildUp;
}
export function freshOverrides() {
    return { disabledLevels: [], empOverrides: {}, groupRenames: {} };
}
export function freshDesign() {
    return {
        buckets: Object.create(null),
        bucketMeta: Object.create(null),
        perimeterThreshold: null,
        tsas: [],
        strandedPlan: Object.create(null),
        heads: { 'as-is': {}, remain: {}, 'carve-out': {} },
        scenarioOverrides: { remain: freshOverrides(), 'carve-out': freshOverrides() },
        buildUps: [],
    };
}
export function freshAssumptions() {
    return {
        loadedCostMultiplier: 1.0,
        severanceMonthsDefault: 6,
        severanceByCountry: {},
        severanceBySeniority: {},
        hiringCostPct: 20,
        retentionPct: 0,
        loadedCostMultiplierByCountry: {},
        fxRates: {},
        reportingCurrency: null,
        day1Date: null,
        consultationMonthsByCountry: {},
        closeMonthByCountry: {},
    };
}
/**
 * Merge an imported assumption set over the defaults, so a file written by an older version — with
 * fewer keys — still lands with every assumption defined.
 */
export function mergeAssumptions(incoming) {
    const base = freshAssumptions();
    if (!incoming || typeof incoming !== 'object')
        return base;
    const a = incoming;
    return {
        loadedCostMultiplier: numberOr(a.loadedCostMultiplier, base.loadedCostMultiplier),
        severanceMonthsDefault: numberOr(a.severanceMonthsDefault, base.severanceMonthsDefault),
        severanceByCountry: plainMap(a.severanceByCountry),
        severanceBySeniority: plainMap(a.severanceBySeniority),
        hiringCostPct: numberOr(a.hiringCostPct, base.hiringCostPct),
        retentionPct: numberOr(a.retentionPct, base.retentionPct),
        loadedCostMultiplierByCountry: plainMap(a.loadedCostMultiplierByCountry),
        fxRates: plainMap(a.fxRates),
        // A label, so blank and absent both mean "unnamed" — there is no legacy encoding to preserve.
        reportingCurrency: typeof a.reportingCurrency === 'string' && a.reportingCurrency.trim() !== ''
            ? a.reportingCurrency
            : null,
        // Kept only when it still parses as the shape every writer produces; a corrupted date label is
        // worse than an undated project, because every rendered month would carry it.
        day1Date: typeof a.day1Date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(a.day1Date) ? a.day1Date : null,
        consultationMonthsByCountry: plainMap(a.consultationMonthsByCountry),
        closeMonthByCountry: plainMap(a.closeMonthByCountry),
    };
}
function numberOr(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}
/**
 * An assumption grid as the file wrote it.
 *
 * Deliberately preserving, including the blank tiers. `severanceMonths` reads three encodings as
 * inherit — key absent, value `null`, value `''` (`cost.ts:47-51`) — while every WRITER produces only
 * absence, and it is tempting to normalise the other two away here. It would be wrong: this is the
 * import path, and its job is to return what the file said, so a project handed back to the app it
 * came from is the project it came from. The asymmetry is handled where it can actually bite —
 * `setAssumption` records a blank tier's `before` as null, so undoing a later override removes the
 * tier rather than restoring an encoding no writer can create (`commands/settings.ts`).
 */
function plainMap(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return {};
    const out = {};
    for (const [k, v] of Object.entries(value)) {
        if (k === '__proto__' || k === 'constructor' || k === 'prototype')
            continue;
        if (typeof v === 'number' || typeof v === 'string')
            out[k] = v;
    }
    return out;
}
export function freshState() {
    return {
        employees: [],
        rows: [],
        excelColumns: [],
        entities: [],
        groupingLevels: [],
        columnMap: {},
        assumptions: freshAssumptions(),
        design: freshDesign(),
        targetEnabled: false,
        targetLevels: [],
        targetUnits: [],
        targetOverrides: { target: freshOverrides() },
    };
}
/** The whole population the aggregates walk: imported people plus planned hires. */
export function allPeople(state) {
    return state.employees.concat(state.design.buildUps);
}
/** Safe own-property read: a person id equal to an Object.prototype member must not inherit. */
export function hasOwn(o, k) {
    return o != null && Object.prototype.hasOwnProperty.call(o, k);
}
//# sourceMappingURL=domain.js.map